import { useFragment } from '@apollo/client';
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { useRevokePortalioConnection } from 'src/graphql/entities/integrations/portalio/hooks/use-revoke-portalio-connection';
import { PORTALIO_AUTH_DETAILS_FRAGMENT } from 'src/graphql/entities/integrations/portalio/portalio.fragments';

export type PortalioIntegrationProps = {
  id: string;
};

export type PortalioAuthDetail = {
  userId: string;
  portalioUserName: string;
};

export const PortalioAuthDetails = ({ id }: PortalioIntegrationProps) => {
  const theme = useTheme();
  const { revokeConnection } = useRevokePortalioConnection();

  const { data, complete } = useFragment<PortalioAuthDetail>({
    fragment: PORTALIO_AUTH_DETAILS_FRAGMENT,
    from: {
      __typename: 'PortalioAuthDetails',
      id,
    },
  });

  const onRevokeHandler = useCallback(async () => {
    await revokeConnection(data.userId!, id);
  }, [data.userId, id, revokeConnection]);

  if (!complete) {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<Avatar alt="portalio-logo" src="/portalio-logo.png" />}
        title="Portal.io"
        subheader="Connected"
        slotProps={{
          subheader: {
            color: theme.palette.success.main,
          },
        }}
      />
      <CardContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          User name
        </Typography>
        <Typography variant="body1">{data.portalioUserName}</Typography>
      </CardContent>
      <CardActions sx={{ m: theme.spacing() }}>
        <Button variant="outlined" onClick={onRevokeHandler} size="small" fullWidth color="error">
          Disconnect
        </Button>
      </CardActions>
    </Card>
  );
};
